import React, { useEffect, useMemo, useState } from "react";
import { api, API, tokenStore } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { FileText, Plus, Trash2, Download, Pencil, Image as ImageIcon } from "lucide-react";

const MONTH_NAMES = ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];
const monthLabel = (m) => {
  const [y, mo] = (m || "").split("-");
  return `${MONTH_NAMES[parseInt(mo, 10) - 1] || m} ${y}`;
};
const fmtD = (iso) => (iso ? iso.split("-").reverse().join(".") : "");
const fileUrl = (id, download = false) =>
  `${API}/fise-ore/${id}/file?token=${encodeURIComponent(tokenStore.get() || "")}${download ? "&download=true" : ""}`;

export default function FiseOre() {
  const [months, setMonths] = useState([]);
  const [selMonth, setSelMonth] = useState(null);
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [upFiles, setUpFiles] = useState([]);
  const [upDate, setUpDate] = useState(new Date().toISOString().slice(0, 10));
  const [upTitle, setUpTitle] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [viewing, setViewing] = useState(null);   // record în vizualizare
  const [editing, setEditing] = useState(null);   // record în editare {id, date, title}

  const loadMonths = () =>
    api.get("/fise-ore/months").then((r) => {
      setMonths(r.data || []);
      setSelMonth((cur) => cur || r.data?.[0]?.month || null);
    }).catch(() => {});
  useEffect(() => { loadMonths(); }, []);
  useEffect(() => {
    if (!selMonth) { setRows([]); return; }
    api.get(`/fise-ore?month=${selMonth}`).then((r) => setRows(r.data || [])).catch(() => {});
  }, [selMonth]);

  const refresh = () => {
    loadMonths();
    if (selMonth) api.get(`/fise-ore?month=${selMonth}`).then((r) => setRows(r.data || [])).catch(() => {});
  };

  const submitUpload = async () => {
    if (upFiles.length === 0) return toast.error("Alege cel puțin un fișier (poze sau PDF)");
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of upFiles) fd.append("files", f);
      fd.append("doc_date", upDate);
      if (upTitle.trim()) fd.append("title", upTitle.trim());
      const { data } = await api.post("/fise-ore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.uploaded} ${data.uploaded === 1 ? "fișier încărcat" : "fișiere încărcate"}`);
      setShowUpload(false); setUpFiles([]); setUpTitle("");
      setSelMonth(data.month);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcare");
    } finally { setUploading(false); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/fise-ore/${editing.id}`, { date: editing.date, title: editing.title });
      toast.success("Modificat");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Ștergi fișierul „${r.title || r.filename}"?`)) return;
    try {
      await api.delete(`/fise-ore/${r.id}`);
      toast.success("Șters");
      setViewing(null);
      refresh();
    } catch (e) { toast.error("Eroare la ștergere"); }
  };

  // Grupare pe zile în luna selectată
  const byDay = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      if (!m.has(r.date)) m.set(r.date, []);
      m.get(r.date).push(r);
    }
    return Array.from(m.entries());
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="fise-ore-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">🗂 Fișe ore</h1>
          <p className="text-sm text-zinc-500">Poze și PDF-uri cu fișele de ore, organizate pe luni și zile.</p>
        </div>
        <Button onClick={() => setShowUpload(true)} className="bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="fise-add-btn">
          <Plus size={16} className="mr-1" /> Adaugă fișă
        </Button>
      </div>

      {/* Căsuțe pe luni */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {months.length === 0 && (
          <div className="col-span-full text-sm text-zinc-400 italic border border-dashed border-zinc-300 rounded-lg p-6 text-center" data-testid="fise-empty">
            Nicio fișă încărcată încă. Apasă „Adaugă fișă".
          </div>
        )}
        {months.map((m) => (
          <button
            key={m.month}
            onClick={() => setSelMonth(m.month)}
            className={`text-left border rounded-lg p-3 transition-colors ${selMonth === m.month ? "border-red-500 bg-red-50 ring-1 ring-red-300" : "border-zinc-200 bg-white hover:border-zinc-400"}`}
            data-testid={`fise-month-${m.month}`}
          >
            <div className="font-bold text-sm">{monthLabel(m.month)}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{m.count} {m.count === 1 ? "fișier" : "fișiere"}</div>
          </button>
        ))}
      </div>

      {/* Fișierele lunii selectate, în ordinea zilelor */}
      {selMonth && (
        <div className="space-y-4" data-testid="fise-list">
          <h2 className="text-lg font-bold">{monthLabel(selMonth)}</h2>
          {byDay.length === 0 && <div className="text-sm text-zinc-400 italic">Nicio fișă în această lună.</div>}
          {byDay.map(([d, files]) => (
            <div key={d} className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
              <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 text-xs font-bold uppercase tracking-wider text-zinc-600">
                📅 {fmtD(d)}
              </div>
              <div className="p-3 flex flex-wrap gap-3">
                {files.map((r) => (
                  <div key={r.id} className="w-32 group cursor-pointer" onClick={() => setViewing(r)} data-testid={`fise-item-${r.id}`}>
                    {r.is_pdf ? (
                      <div className="w-32 h-32 rounded-md border border-zinc-200 bg-red-50 flex flex-col items-center justify-center gap-1 group-hover:border-red-400 transition-colors">
                        <FileText size={34} className="text-red-600" />
                        <span className="text-[10px] font-bold text-red-700">PDF</span>
                      </div>
                    ) : (
                      <img src={fileUrl(r.id)} alt="" className="w-32 h-32 rounded-md object-cover border border-zinc-200 group-hover:border-red-400 transition-colors" loading="lazy" />
                    )}
                    <div className="mt-1 text-[11px] font-semibold truncate" title={r.title || r.filename}>{r.title || r.filename}</div>
                    <div className="text-[10px] text-zinc-400">{r.created_by}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog încărcare */}
      {showUpload && (
        <Dialog open onOpenChange={(o) => !o && setShowUpload(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>🗂 Adaugă fișă ore</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="small-label">Fișiere (poze sau PDF — poți selecta mai multe deodată)</Label>
                <Input type="file" accept="image/*,application/pdf" multiple className="mt-1"
                  onChange={(e) => setUpFiles((prev) => [...prev, ...Array.from(e.target.files || [])])} data-testid="fise-upload-file" />
                {upFiles.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-36 overflow-auto" data-testid="fise-upload-selected">
                    {upFiles.map((f, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-zinc-50 border border-zinc-200 rounded px-2 py-1">
                        <span className="truncate font-semibold text-emerald-700">✓ {f.name}</span>
                        <button type="button" className="text-red-500 font-bold ml-2 shrink-0"
                          onClick={() => setUpFiles((prev) => prev.filter((_, k) => k !== i))}>✕</button>
                      </div>
                    ))}
                    <div className="text-[11px] text-zinc-500 font-semibold">{upFiles.length} {upFiles.length === 1 ? "fișier selectat" : "fișiere selectate"}</div>
                  </div>
                )}
              </div>
              <div>
                <Label className="small-label">Data fișei</Label>
                <Input type="date" value={upDate} onChange={(e) => setUpDate(e.target.value)} className="mt-1" data-testid="fise-upload-date" />
              </div>
              <div>
                <Label className="small-label">Titlu (opțional)</Label>
                <Input value={upTitle} onChange={(e) => setUpTitle(e.target.value)} placeholder="ex: Fișă ore echipa Imas" className="mt-1" data-testid="fise-upload-title" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUpload(false)}>Anulează</Button>
              <Button onClick={submitUpload} disabled={uploading} className="bg-red-600 hover:bg-red-700 text-white" data-testid="fise-upload-submit">
                {uploading ? "Se încarcă..." : "Încarcă"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog vizualizare */}
      {viewing && (
        <Dialog open onOpenChange={(o) => !o && setViewing(null)}>
          <DialogContent className="max-w-3xl" data-testid="fise-viewer">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {viewing.is_pdf ? <FileText size={18} className="text-red-600" /> : <ImageIcon size={18} className="text-blue-600" />}
                {viewing.title || viewing.filename}
                <span className="text-xs font-normal text-zinc-400">· {fmtD(viewing.date)}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[65vh] overflow-auto rounded-md border border-zinc-200 bg-zinc-50 flex items-center justify-center">
              {viewing.is_pdf ? (
                <iframe title="pdf" src={fileUrl(viewing.id)} className="w-full h-[62vh]" />
              ) : (
                <img src={fileUrl(viewing.id)} alt="" className="max-w-full max-h-[62vh] object-contain" />
              )}
            </div>
            <DialogFooter className="flex-wrap gap-2">
              <a href={fileUrl(viewing.id, true)} download>
                <Button variant="outline" data-testid="fise-download-btn"><Download size={15} className="mr-1" /> Descarcă</Button>
              </a>
              <Button variant="outline" onClick={() => { setEditing({ id: viewing.id, date: viewing.date, title: viewing.title || "" }); setViewing(null); }} data-testid="fise-edit-btn">
                <Pencil size={15} className="mr-1" /> Editează
              </Button>
              <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => remove(viewing)} data-testid="fise-delete-btn">
                <Trash2 size={15} className="mr-1" /> Șterge
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog editare */}
      {editing && (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-sm" data-testid="fise-edit-dialog">
            <DialogHeader><DialogTitle>✏️ Editează fișa</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="small-label">Data fișei</Label>
                <Input type="date" value={editing.date} onChange={(e) => setEditing((p) => ({ ...p, date: e.target.value }))} className="mt-1" data-testid="fise-edit-date" />
              </div>
              <div>
                <Label className="small-label">Titlu</Label>
                <Input value={editing.title} onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))} className="mt-1" data-testid="fise-edit-title" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Anulează</Button>
              <Button onClick={saveEdit} className="bg-red-600 hover:bg-red-700 text-white" data-testid="fise-edit-save">Salvează</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
